import { useRef } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type {
  HydratedOpportunity,
  OpportunityBoardFilters,
} from "@forgepoint/shared";
import {
  OPPORTUNITY_DELTA_LIMIT,
  useOpportunitiesStore,
} from "./opportunitiesStore";
import { useOpportunityLivenessPoll } from "./useOpportunityLivenessPoll";
import {
  applyOpportunityDeltaToColumns,
  DELTA_CURSOR_OVERLAP_MS,
  maxUpdatedAt,
  type ColumnState,
} from "./liveBoardSync";
import type { OpportunityArchiveMode } from "./opportunityFilterTypes";

interface UseLiveBoardSyncOptions {
  /** True once the board has completed its initial column fetch. */
  hasInitiallyFetchedRef: MutableRefObject<boolean>;
  /** Always-current column state, read after awaits instead of stale closures. */
  columnStatesRef: MutableRefObject<Map<string, ColumnState>>;
  /** The resolved selections (person, created range, archive flags). */
  viewFiltersRef: MutableRefObject<OpportunityBoardFilters>;
  archiveModeRef: MutableRefObject<OpportunityArchiveMode>;
  /** The raw search input; normalized once per tick. */
  searchValue: string;
  setColumnStates: Dispatch<SetStateAction<Map<string, ColumnState>>>;
  /** Full-board refetch, used when a delta overflows one poll's carrying capacity. */
  fetchAllColumns: (search?: string) => Promise<void>;
  refreshStatistics: () => void;
  refreshFilterCounts: () => void;
}

/**
 * Keeps the opportunities board live: rides the 5s liveness poll to fetch
 * opportunities changed since the last tick (the delta cursor, seeded and
 * advanced from server timestamps so client clock skew is irrelevant) and
 * applies them to the board — new cards appear, stage moves relocate, and
 * archived or filtered-out cards leave, all without a manual refresh. Changes
 * that arrive while a card is being dragged are buffered and applied when the
 * drag settles, so dnd-kit is never disturbed mid-drag.
 * @param options - Board state accessors and refresh callbacks
 * @returns The drag-state handler to pass to the kanban board
 */
export function useLiveBoardSync({
  hasInitiallyFetchedRef,
  columnStatesRef,
  viewFiltersRef,
  archiveModeRef,
  searchValue,
  setColumnStates,
  fetchAllColumns,
  refreshStatistics,
  refreshFilterCounts,
}: UseLiveBoardSyncOptions): {
  handleDragActiveChange: (active: boolean) => void;
} {
  const fetchOpportunityDelta = useOpportunitiesStore(
    (state) => state.fetchOpportunityDelta,
  );
  const deltaCursorRef = useRef<Date | null>(null);
  const isDragActiveRef = useRef(false);
  const pendingDeltaRef = useRef<Map<string, HydratedOpportunity>>(new Map());

  /**
   * One live-sync tick: fetches opportunities changed since the delta cursor
   * and applies them to the board. A full-limit response means more changed
   * than one poll can carry, so the whole board is refetched instead.
   */
  const handleLiveTick = async () => {
    if (
      !hasInitiallyFetchedRef.current ||
      useOpportunitiesStore.getState().listLoading
    ) {
      return;
    }
    const search = searchValue.trim() || undefined;
    const cursor = deltaCursorRef.current ?? seedDeltaCursor();
    deltaCursorRef.current = cursor;
    const changed = await fetchOpportunityDelta(
      new Date(cursor.getTime() - DELTA_CURSOR_OVERLAP_MS),
      search,
      viewFiltersRef.current,
    );
    if (changed === null) {
      return;
    }
    deltaCursorRef.current = maxUpdatedAt(changed, cursor);
    if (changed.length === 0) {
      return;
    }
    if (changed.length >= OPPORTUNITY_DELTA_LIMIT) {
      await fetchAllColumns(search);
      return;
    }
    if (isDragActiveRef.current) {
      changed.forEach((opp) => pendingDeltaRef.current.set(opp.id, opp));
      return;
    }
    applyLiveDelta(changed);
  };

  /**
   * Seeds the delta cursor from the freshest record already loaded, so the
   * first delta poll only reports changes newer than the initial board load.
   */
  const seedDeltaCursor = (): Date => {
    const loaded = Array.from(
      useOpportunitiesStore.getState().opportunities.values(),
    );
    if (loaded.length === 0) {
      return new Date();
    }
    return maxUpdatedAt(loaded, new Date(0));
  };

  /**
   * Applies changed opportunities to the column state and refreshes the
   * statistics bar; filter counts refresh only when board membership changed.
   * The state update is functional and the underlying column writes are
   * idempotent, so a delta echoing the user's own optimistic move (drag,
   * archive, create) composes with it instead of duplicating the card.
   */
  const applyLiveDelta = (changed: HydratedOpportunity[]) => {
    const { membershipChanged } = applyOpportunityDeltaToColumns(
      columnStatesRef.current,
      changed,
      viewFiltersRef.current,
      archiveModeRef.current,
    );
    setColumnStates(
      (prev) =>
        applyOpportunityDeltaToColumns(
          prev,
          changed,
          viewFiltersRef.current,
          archiveModeRef.current,
        ).columns,
    );
    if (membershipChanged) {
      refreshFilterCounts();
    }
    refreshStatistics();
  };

  /**
   * Tracks whether a card drag is in flight and flushes any live changes that
   * were buffered while the user was dragging.
   */
  const handleDragActiveChange = (active: boolean) => {
    isDragActiveRef.current = active;
    if (active || pendingDeltaRef.current.size === 0) {
      return;
    }
    const pending = Array.from(pendingDeltaRef.current.values());
    pendingDeltaRef.current = new Map();
    applyLiveDelta(pending);
  };

  useOpportunityLivenessPoll(handleLiveTick);

  return { handleDragActiveChange };
}
